import { useRef, useState } from 'react';
import type { QuestionAnswerInput } from '../api/client';
import type { Problem, SessionDetail, StageCondition, StudioMessage, StudioQuestion, StudioTarget } from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { ProblemNotice } from '../components/ProblemNotice';
import { usePlatform } from '../app/platform';
import { turnStatusView } from '../app/status';
import { toProblem } from '../app/useAsync';
import { useWaitingTitle } from '../app/useWaitingTitle';
import { MessageList } from './MessageList';
import { StageAdvance } from './StageAdvance';
import { ThinkingLine } from './ThinkingLine';

/** 置けるファイルの拡張子。知識ソース画面と同じ集合（API の studio/domain/knowledge.py の MEDIA_TYPES）。 */
const ACCEPT_SUFFIXES = [
  '.pdf', '.md', '.txt', '.html', '.htm', '.csv', '.json',
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.docx', '.xlsx', '.pptx',
];
const ACCEPT = ACCEPT_SUFFIXES.join(',');
/** 1 回のターンに渡せる添付の数（API の TurnCreate.attachments の maxItems）。 */
const MAX_ATTACHMENTS = 10;
/** 1 ファイルの上限（API の studio/domain/knowledge.py の MAX_FILE_BYTES と同じ 25 MiB）。 */
const MAX_FILE_BYTES = 25 * 1024 * 1024;

/** 置き終えて確定した資料。itemId はアップロードしたファイルの項目の id。 */
interface Attachment {
  itemId: string;
  name: string;
}

/**
 * 置く前に弾く理由（U2）。api の :initialize は宣言をまとめて検査して 1 つでも駄目なら Problem を投げるので、
 * ここで先に振り分けないと 1 件の不備で束ごと失敗し、どのファイルが原因か画面に出ない。
 * drop には input の accept が掛からないので、選択と drop の両方でこれを通す。
 */
function fileProblem(file: File): string | null {
  const dot = file.name.lastIndexOf('.');
  const suffix = dot < 0 ? '' : file.name.slice(dot).toLowerCase();
  if (!ACCEPT_SUFFIXES.includes(suffix)) return '対応していない形式です';
  if (file.size < 1 || file.size > MAX_FILE_BYTES) return '大きさは 1 byte〜25 MiB です';
  return null;
}

export interface ChatPaneProps {
  workspaceId: string;
  /** 最新のセッション。まだ無ければ null（最初の送信でセッションを作る）。 */
  detail: SessionDetail | null;
  messages: StudioMessage[];
  /** これまでに見た質問（useStudioSession.questions）。回答済みの 1 行に質問文を出す。 */
  questions: StudioQuestion[];
  /** 文脈チップ。空なら Workspace 全体。 */
  target: StudioTarget;
  appName: string | null;
  onClearTarget(): void;
  /** 失敗したら投げる。入力を残して理由を出す。attachments は添付した資料の itemId（U2）。 */
  onSend(text: string, attachments: string[]): Promise<void>;
  onAnswer(questionId: string, answers: QuestionAnswerInput[]): Promise<void>;
  onCancel(): Promise<void>;
  onResume(): Promise<void>;
  /** Draft の段階（1〜6）。null なら Draft が無い（アクション行を出さない。U7）。 */
  stage?: number | null;
  /** 次へ進む条件（GET A/draft/review の conditions）。段階バーに渡すものと同じ。 */
  stageConditions?: StageCondition[];
  /** 段階の移動か固定文のターンの送信を要求中（段階バーと共有）。 */
  stageBusy?: boolean;
  /** 未反映の変更案がある（進めると反映できなくなるので押させない。U7）。 */
  proposalPending?: boolean;
  /** 「段階 n（段階名）へ進む」を押したとき。渡さなければアクション行を出さない。 */
  onAdvanceStage?(): Promise<void>;
  /** AI の変更がターンの終わりに自動で反映される appId（公開前の Draft。U8）。 */
  autoApplyApps?: ReadonlySet<string>;
  /** 反映済みの行から版の履歴（microapp のコードタブ）へ移る。 */
  onOpenHistory?(appId: string): void;
}

/** AI との対話（Claude Design のチャット列）。見出しと状態、会話、文脈チップと入力欄。 */
export function ChatPane(props: ChatPaneProps) {
  const { api, mode } = usePlatform();
  const { workspaceId, detail, messages, questions, target, appName } = props;
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const composer = useRef<HTMLTextAreaElement>(null);
  // 添付（U2）。置き終えて ready になったものだけをチップにする。
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const pane = useRef<HTMLDivElement>(null);

  const turn = detail?.currentTurn ?? null;
  const working = turn !== null && (turn.status === 'queued' || turn.status === 'running');
  const waiting = detail?.session.status === 'waiting_answer';
  // 別のタブを見ていても気づけるように、回答待ちの間だけ見出しに印を付ける（U6）。
  useWaitingTitle(waiting === true);
  const status = turn ? turnStatusView(turn.status) : null;
  // fixture モードには知識ソースの API が無いので、添付の導線を出さない（C45）。
  const canAttach = mode !== 'fixture';
  // 段階を進めるアクション行（U7）。AI が動いている間（queued / running / waiting_answer）は出さない。
  // detail は送信の 1 往復ぶん古いことがあるので、working と sending も見る（押せば 429 turn_limit になる窓を閉じる）。
  // fixture モードには Studio の API が無いので段階も動かせない（C45）。
  const onAdvanceStage = props.onAdvanceStage;
  const canAdvance =
    mode !== 'fixture' && !working && !sending && (detail === null || detail.session.status === 'idle');
  const placeholder = working
    ? 'AI の作業が終わるまで送信は待ちます。中断して指示できます'
    : waiting
      ? '質問に答える か、別の指示を書く…'
      : 'このフローで変えたいことを伝える…';

  // 詳細の open な質問を、覚えている質問より優先する（状態が新しい）。ただし、いま回答した質問は
  // 詳細を取り直すまで open のまま返ってくるので、閉じた写しがあればそちらを残す（二重送信を防ぐ）。
  const known = new Map(questions.map((question) => [question.id, question]));
  for (const question of detail?.openQuestions ?? []) {
    if (known.get(question.id)?.status !== 'open' && known.has(question.id)) continue;
    known.set(question.id, question);
  }

  const run = async (action: () => Promise<void>) => {
    setProblem(null);
    try {
      await action();
    } catch (error) {
      setProblem(toProblem(error));
    }
  };

  /**
   * 選んだ・落とした資料を知識ソースへ置く（受付 → 本体 → 確定の 3 段階）。
   * 置ける形と大きさのものだけを送り、確定した項目を添付のチップにする。
   * 弾いたもの・確定できなかったものは名前と理由を並べる。
   */
  const place = async (chosen: File[]) => {
    if (uploading) return;
    // 同じファイルを選び直せるよう、どの枝を通っても選択を空に戻す（戻さないと change が起きない）。
    const clearPicker = () => {
      if (picker.current !== null) picker.current.value = '';
    };
    if (chosen.length === 0) {
      clearPicker();
      return;
    }
    setProblem(null);
    const refused: string[] = [];
    const usable: File[] = [];
    // 同じ名前が 2 件あると api の :initialize が束ごと 422 duplicate_file_name にするので、
    // 2 件目から先に弾く（名前の揃え方は api の normalize_file_name と同じ strip ＋ NFC。U2）。
    const seen = new Set<string>();
    for (const file of chosen) {
      const reason = fileProblem(file);
      const key = file.name.trim().normalize('NFC');
      if (reason !== null) refused.push(`${file.name}: ${reason}`);
      else if (seen.has(key)) refused.push(`${file.name}: 同じ名前の資料が重複しています`);
      else {
        seen.add(key);
        usable.push(file);
      }
    }
    const room = Math.max(0, MAX_ATTACHMENTS - attachments.length);
    const files = usable.slice(0, room);
    for (const file of usable.slice(room)) refused.push(`${file.name}: 添付は ${MAX_ATTACHMENTS} 件までです`);
    if (files.length === 0) {
      setRejected(refused);
      clearPicker();
      return;
    }
    setRejected([]);
    setUploading(true);
    try {
      const { result, uploads } = await api.uploadKnowledgeFiles(workspaceId, files);
      const invalids = new Map(result.invalids.map((entry) => [entry.fileId, entry.reason]));
      setAttachments((current) => {
        const byId = new Map(current.map((item) => [item.itemId, item]));
        for (const entry of uploads) {
          if (invalids.has(entry.fileId)) continue;
          byId.set(entry.fileId, { itemId: entry.fileId, name: entry.name });
        }
        return [...byId.values()];
      });
      setRejected([
        ...refused,
        ...uploads
          .filter((entry) => invalids.has(entry.fileId))
          .map((entry) => `${entry.name}: ${invalids.get(entry.fileId) ?? ''}`),
      ]);
    } catch (error) {
      setProblem(toProblem(error));
      setRejected(refused);
    } finally {
      setUploading(false);
      clearPicker();
    }
  };

  const send = async () => {
    const value = text.trim();
    if (value === '' || working || sending || uploading) return;
    setSending(true);
    setProblem(null);
    try {
      await props.onSend(
        value,
        attachments.map((item) => item.itemId),
      );
      setText('');
      setAttachments([]);
      setRejected([]);
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setSending(false);
    }
  };

  const composerClass = ['studio-composer'];
  if (waiting) composerClass.push('studio-composer-waiting');
  if (dragging) composerClass.push('studio-composer-drop');

  /** 帯から、開いている質問カードへ移る（U6）。 */
  const showQuestion = () => {
    const card = pane.current?.querySelector('[data-question-id]');
    if (card instanceof HTMLElement) card.scrollIntoView({ block: 'nearest' });
  };

  return (
    <div className="studio-chat-pane" ref={pane}>
      <div className="studio-chat-head">
        <h2 className="studio-chat-title">Workspace Assistant</h2>
        {status && <Badge tone={status.tone}>{status.label}</Badge>}
      </div>
      {waiting && (
        <button type="button" className="studio-waiting-banner" onClick={showQuestion}>
          AI があなたの回答を待っています
        </button>
      )}
      <MessageList
        messages={messages}
        questions={[...known.values()]}
        onAnswer={props.onAnswer}
        onResume={() => void run(props.onResume)}
        onFocusComposer={() => composer.current?.focus()}
        autoApplyApps={props.autoApplyApps}
        onOpenHistory={props.onOpenHistory}
      />
      {working && turn !== null && (
        <ThinkingLine turn={turn} toolCalls={messages.filter((message) => message.kind === 'tool_call' && message.turnId === turn.id)} />
      )}
      {canAdvance && onAdvanceStage !== undefined && (
        <StageAdvance
          stage={props.stage ?? null}
          conditions={props.stageConditions ?? []}
          busy={props.stageBusy === true}
          proposalPending={props.proposalPending === true}
          onAdvance={onAdvanceStage}
        />
      )}
      <form
        className={composerClass.join(' ')}
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
        onDragOver={(event) => {
          if (!canAttach) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          if (!canAttach) return;
          event.preventDefault();
          setDragging(false);
          void place(Array.from(event.dataTransfer.files));
        }}
      >
        <div className="row">
          {target.appId ? (
            <span className="studio-chip">
              <span>{`${appName ?? target.appId}${target.elementId ? ` / ${target.elementId}` : ''}`}</span>
              <button type="button" className="studio-chip-clear" aria-label="対象をWorkspace全体に戻す" onClick={props.onClearTarget}>
                ×
              </button>
            </span>
          ) : (
            <span className="studio-chip">
              <span>Workspace 全体</span>
            </span>
          )}
          <span className="spacer" />
          {canAttach && (
            <label className="studio-attach-pick">
              <span className="studio-attach-pick-label">資料を添付</span>
              <input
                ref={picker}
                className="studio-attach-input"
                type="file"
                multiple
                accept={ACCEPT}
                disabled={uploading}
                onChange={(event) => void place(Array.from(event.target.files ?? []))}
              />
            </label>
          )}
        </div>
        {canAttach && attachments.length > 0 && (
          <ul className="studio-attach-list" aria-label="添付">
            {attachments.map((item) => (
              <li key={item.itemId} className="studio-chip">
                <span>{item.name}</span>
                <button
                  type="button"
                  className="studio-chip-clear"
                  aria-label={`${item.name} の添付を取り消す`}
                  onClick={() => setAttachments((current) => current.filter((entry) => entry.itemId !== item.itemId))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        {uploading && (
          <p className="hint" role="status">
            資料を置いています…
          </p>
        )}
        {rejected.length > 0 && (
          <ul className="studio-attach-failed" role="alert">
            {rejected.map((line, index) => (
              // 同じ名前・同じ理由の 2 件で key が重なるので添字を混ぜる。
              <li key={`${index}:${line}`}>{line}</li>
            ))}
          </ul>
        )}
        <textarea
          ref={composer}
          className="studio-composer-input"
          aria-label="AI への指示"
          placeholder={placeholder}
          value={text}
          rows={3}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            // Ctrl / ⌘ + Enter で送る。Enter だけは改行（IME の確定と区別する）。
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void send();
            }
          }}
        />
        <ProblemNotice problem={problem} tone={problem && (problem.status === 409 || problem.status === 429) ? 'notice' : 'error'} />
        <div className="row">
          {canAttach && <span className="hint">資料はここへドラッグしても置けます</span>}
          {working && <Button onClick={() => void run(props.onCancel)}>中断</Button>}
          <span className="spacer" />
          <Button type="submit" variant="primary" disabled={working || sending || uploading || text.trim() === ''}>
            送信
          </Button>
        </div>
      </form>
    </div>
  );
}
